/**
 * DAG Service - Directed Acyclic Graph with multiple parents per node
 *
 * Extends CollectionService with:
 * - Junction table for N:N parent relationships ({table}_parents)
 * - Cycle detection for graphs (validates no circular references)
 * - Parent management operations (get/set/add/remove parents)
 * - Tree-like traversal adapted for DAG structure
 *
 * Delegation: graph traversal and parent mutations are delegated to
 * IDagRepository<T>. The DagService preserves JSON-field parsing,
 * service-level validation (parent existence), event emission and seeding.
 *
 * Column reconciliation: legacy DagService used `{table}_parents` with
 * columns `node_id`/`parent_id`. The default KnexDagRepository convention
 * is `{table}_links` with `child_id`/`parent_id`. We override `buildRepo()`
 * to pass the legacy names so the existing DB schema keeps working.
 *
 * @incompatible RedisAdapter - requires foreign key support
 */

import type {
  PaginatedResult,
  ModuleContext,
  EntityServiceHooks,
  DagEntityDefinition,
  CollectionEntityDefinition,
  TreeNode
} from '@gzl10/nexus-sdk'
import type { EntityQuery } from '@kernel/runtime/index.js'
import { CollectionService } from './collection.service.js'
import type { EntityRepository, IDagRepository } from '../repositories/types.js'
import { KnexDagRepository } from '../repositories/knex/index.js'
import { loadSeedData } from '../helpers/index.js'

export class DagService<T extends Record<string, unknown> = Record<string, unknown>>
  extends CollectionService<T> {

  /** Original DAG entity definition (before conversion to collection) */
  readonly dagDefinition: DagEntityDefinition
  /** Name of the parents junction table */
  readonly parentsTableName: string

  constructor(ctx: ModuleContext, definition: DagEntityDefinition, hooks?: EntityServiceHooks<T>, repo?: EntityRepository) {
    // Convert to collection-like definition for base class
    const collectionDef = {
      ...definition,
      type: 'collection' as const,
      labelField: definition.labelField ?? 'name'
    } as CollectionEntityDefinition

    // Build dag repo with reconciled column names (legacy: node_id / parent_id
    // in *_parents). Skip if a repo is provided or if context is not table-backed.
    let resolvedRepo: EntityRepository | undefined = repo
    if (!resolvedRepo) {
      try {
        const adapterName = (definition as unknown as { adapter?: string }).adapter
        const knex = ctx.db.getKnex(adapterName)
        const fields = (definition as unknown as { fields?: Record<string, { meta?: { searchable?: boolean } }> }).fields
        const searchableFields = fields
          ? Object.entries(fields)
              .filter(([, f]) => f?.meta?.searchable === true)
              .map(([name]) => name)
          : []
        const defaultSort = (definition as unknown as { defaultSort?: { field: string; order: 'asc' | 'desc' } }).defaultSort ?? null
        const parentsTable = definition.parentsTable ?? `${definition.table}_parents`

        resolvedRepo = new KnexDagRepository(knex, definition.table, {
          searchableFields,
          defaultSort,
          labelField: definition.labelField,
          fieldDefs: fields as Record<string, never> | undefined,
          linkTable: parentsTable,
          childField: 'node_id',
          parentField: 'parent_id',
        })
      } catch {
        // Fallback for non-table-backed contexts (tests may inject repo later)
        resolvedRepo = undefined
      }
    }

    super(ctx, collectionDef, hooks, resolvedRepo)

    // Store original DAG definition
    this.dagDefinition = definition
    this.parentsTableName = definition.parentsTable ?? `${definition.table}_parents`

    // Validate adapter compatibility at construction time
    this.validateAdapterCompatibility()
  }

  /**
   * Validate that the adapter is compatible with DAG entities.
   * Some adapters don't support foreign keys required for parent relationships.
   */
  private validateAdapterCompatibility(): void {
    const adapterName = (this.definition as unknown as { adapter?: string }).adapter
    if (adapterName === 'temp' || adapterName === 'redis') {
      throw new Error(
        `DagEntityDefinition '${this.definition.table}' is incompatible with adapter '${adapterName}'. ` +
        `DAG entities require foreign key support for parent relationships.`
      )
    }
  }

  /**
   * Get all DAG nodes with optional filtering
   */
  override async findAll(query?: EntityQuery): Promise<PaginatedResult<T>> {
    return this.baseFindAll(query)
  }

  // ============================================================================
  // Parents Management — delegated to IDagRepository
  // ============================================================================

  /**
   * Get all parents of a node
   */
  async getParents(nodeId: string): Promise<T[]> {
    const repo = this.repo as IDagRepository<T>
    const raw = await repo.getParents(nodeId)
    return this.parseJsonFieldsFromArray(raw as Record<string, unknown>[]) as T[]
  }

  /**
   * Set all parents for a node (replaces existing parents).
   * Validates parent existence at the service layer; cycle detection
   * happens inside the repo (and is rethrown as ValidationError).
   */
  async setParents(nodeId: string, parentIds: string[]): Promise<void> {
    // Service-level validation: all parents must exist
    for (const parentId of parentIds) {
      if (parentId === nodeId) {
        throw new this.errors.ValidationError(
          'A node cannot be its own parent',
          [{ path: 'parent_id', message: 'Self-reference not allowed' }]
        )
      }
      const parent = await this.findById(parentId)
      if (!parent) {
        throw new this.errors.ValidationError(
          `Parent node '${parentId}' does not exist`,
          [{ path: 'parent_ids', message: 'Parent node not found' }]
        )
      }
    }

    const repo = this.repo as IDagRepository<T>
    try {
      await repo.setParents(nodeId, parentIds)
    } catch (e) {
      this.rethrowCycleAsValidation(e)
    }

    this.emitEntityEvent('updated', undefined, nodeId)
  }

  /**
   * Add a parent to a node (idempotent).
   */
  async addParent(nodeId: string, parentId: string): Promise<void> {
    if (parentId === nodeId) {
      throw new this.errors.ValidationError(
        'A node cannot be its own parent',
        [{ path: 'parent_id', message: 'Self-reference not allowed' }]
      )
    }

    const parent = await this.findById(parentId)
    if (!parent) {
      throw new this.errors.ValidationError(
        `Parent node '${parentId}' does not exist`,
        [{ path: 'parent_id', message: 'Parent node not found' }]
      )
    }

    const repo = this.repo as IDagRepository<T>
    try {
      await repo.addParent(nodeId, parentId)
    } catch (e) {
      this.rethrowCycleAsValidation(e)
    }

    this.emitEntityEvent('updated', undefined, nodeId)
  }

  /**
   * Remove a parent from a node
   */
  async removeParent(nodeId: string, parentId: string): Promise<void> {
    const repo = this.repo as IDagRepository<T>
    await repo.removeParent(nodeId, parentId)
    this.emitEntityEvent('updated', undefined, nodeId)
  }

  /**
   * Translate repo-level cycle errors into service ValidationError.
   */
  private rethrowCycleAsValidation(e: unknown): never {
    if (e instanceof Error && /cycle|own parent/i.test(e.message)) {
      throw new this.errors.ValidationError(
        'Cannot add parent: would create a cycle in the graph',
        [{ path: 'parent_id', message: 'Cycle detected' }]
      )
    }
    throw e
  }

  // ============================================================================
  // Tree Operations (adapted for DAG) — delegated to IDagRepository
  // ============================================================================

  /**
   * Get DAG as tree structure.
   * Note: shared nodes are duplicated under each parent in the resulting tree.
   *
   * @param rootId - Optional root node ID. If provided, returns subtree from that node.
   */
  async getTree(rootId?: string): Promise<TreeNode<T>[]> {
    // Fetch all nodes for the in-memory tree assembly
    const allResult = await this.baseFindAll({ limit: 10000 })
    const items = allResult.items

    const repo = this.repo as IDagRepository<T>
    const parentToChildren = new Map<string, Set<string>>()
    const childToParents = new Map<string, Set<string>>()

    // Build parent/child relations by querying the repo for each node.
    // (DAG repo exposes getParents but no flat link enumerator; this keeps
    // the operation portable across adapters at the cost of N queries.)
    for (const item of items) {
      const id = item['id'] as string
      const parents = await repo.getParents(id)
      if (parents.length > 0) {
        const set = new Set(parents.map(p => p['id'] as string))
        childToParents.set(id, set)
        for (const pid of set) {
          if (!parentToChildren.has(pid)) parentToChildren.set(pid, new Set())
          parentToChildren.get(pid)!.add(id)
        }
      }
    }

    const itemMap = new Map<string, T>()
    for (const item of items) {
      itemMap.set(item['id'] as string, item)
    }

    const rootIds: string[] = []
    for (const item of items) {
      const id = item['id'] as string
      const parents = childToParents.get(id)
      if (!parents || parents.size === 0) {
        rootIds.push(id)
      }
    }

    const buildNode = (id: string, visited: Set<string>): TreeNode<T> | null => {
      if (visited.has(id)) return null
      const item = itemMap.get(id)
      if (!item) return null
      const newVisited = new Set(visited)
      newVisited.add(id)
      const childIds = parentToChildren.get(id) ?? new Set()
      const children: TreeNode<T>[] = []
      for (const childId of childIds) {
        const childNode = buildNode(childId, newVisited)
        if (childNode) children.push(childNode)
      }
      return { data: item, children }
    }

    if (rootId) {
      const node = buildNode(rootId, new Set())
      return node ? [node] : []
    }

    const trees: TreeNode<T>[] = []
    for (const id of rootIds) {
      const tree = buildNode(id, new Set())
      if (tree) trees.push(tree)
    }
    return trees
  }

  /**
   * Get all ancestors of a node (multiple paths to roots).
   */
  async getAncestors(id: string): Promise<T[]> {
    const repo = this.repo as IDagRepository<T>
    const raw = await repo.getAncestors(id)
    return this.parseJsonFieldsFromArray(raw as Record<string, unknown>[]) as T[]
  }

  /**
   * Get all descendants of a node (all children recursively).
   */
  async getDescendants(id: string): Promise<T[]> {
    const repo = this.repo as IDagRepository<T>
    const raw = await repo.getDescendants(id)
    return this.parseJsonFieldsFromArray(raw as Record<string, unknown>[]) as T[]
  }

  /**
   * Find direct children (nodes that have this as a parent)
   */
  async findChildren(parentId: string | null): Promise<T[]> {
    const repo = this.repo as IDagRepository<T>
    const raw = await repo.findChildren(parentId)
    return this.parseJsonFieldsFromArray(raw as Record<string, unknown>[]) as T[]
  }

  /**
   * Get root nodes (nodes with no parents)
   */
  async findRoots(): Promise<T[]> {
    const repo = this.repo as IDagRepository<T>
    const raw = await repo.findRoots()
    return this.parseJsonFieldsFromArray(raw as Record<string, unknown>[]) as T[]
  }

  /**
   * Get the depth of a node in the DAG (minimum path to any root = 0)
   */
  async getDepth(id: string): Promise<number> {
    const visited = new Set<string>()
    const queue: Array<{ id: string; depth: number }> = [{ id, depth: 0 }]

    while (queue.length > 0) {
      const { id: currentId, depth } = queue.shift()!
      if (visited.has(currentId)) continue
      visited.add(currentId)

      const parents = await this.getParents(currentId)
      if (parents.length === 0) {
        return depth
      }
      for (const parent of parents) {
        queue.push({ id: parent['id'] as string, depth: depth + 1 })
      }
    }
    return 0
  }

  /**
   * Check if a node is an ancestor of another node
   */
  async isAncestorOf(ancestorId: string, descendantId: string): Promise<boolean> {
    const ancestors = await this.getAncestors(descendantId)
    return ancestors.some(a => a['id'] === ancestorId)
  }

  // ============================================================================
  // Override delete to also clean up parents table (both directions)
  // ============================================================================

  /**
   * Delete a node and its parent relationships (in either direction).
   *
   * IDagRepository does not expose a "remove all links touching N" primitive,
   * so we use removeParent for the inbound side and rely on getDescendants +
   * removeParent to clean the outbound side.
   */
  override async delete(id: string): Promise<void> {
    const repo = this.repo as IDagRepository<T>

    // Remove inbound links (this node as a child)
    const parents = await repo.getParents(id)
    for (const parent of parents) {
      await repo.removeParent(id, parent['id'] as string)
    }

    // Remove outbound links (this node as a parent of others)
    const directChildren = await repo.findChildren(id)
    for (const child of directChildren) {
      await repo.removeParent(child['id'] as string, id)
    }

    await super.delete(id)
  }

  // ============================================================================
  // Seed
  // ============================================================================

  /**
   * Seed initial DAG data from definition.
   * Inserts nodes that don't exist yet (matched by ID).
   * Supports both inline arrays and URL-based SeedConfig.
   *
   * Seed items can include a `parent_ids` array for parent relationships.
   * IMPORTANT: Seed items should be ordered with parents before children
   * to ensure parent nodes exist when creating relationships.
   *
   * @returns Number of nodes seeded
   */
  override async seed(): Promise<number> {
    const seedData = await loadSeedData(this.dagDefinition.seed, this.logger, 'DAG')
    if (!seedData || seedData.length === 0) {
      return 0
    }

    const repo = this.repo as IDagRepository<T>
    let seeded = 0

    for (const item of seedData) {
      const id = item['id'] as string | undefined
      if (!id) continue

      const exists = await this.findById(id)
      if (!exists) {
        // Extract parent_ids from seed item (DAG-specific)
        const parentIds = item['parent_ids'] as string[] | undefined
        const { parent_ids: _, ...nodeData } = item as Record<string, unknown>

        const data = { ...nodeData, id } as Record<string, unknown>

        if (this.definition.timestamps) {
          const now = this.getNowTimestamp()
          data['created_at'] = now
          data['updated_at'] = now
        }

        // Insert the node
        await this.db(this.table).insert(this.serializeForDb(data))

        // Insert parent relationships via the repo
        if (parentIds && parentIds.length > 0) {
          await repo.setParents(id, parentIds)
        }

        seeded++
      }
    }

    this.logger.info(`DAG data seeded: ${this.table} (${seeded} items)`)
    return seeded
  }
}
